import { Grid, Typography, Stack, Box, FormControlLabel, Checkbox, FormGroup } from "@mui/material";
import Selector from "components/select/selector";
import ToolbarDivider from "components/display/toolbarDivider";
import { useTranslation } from "utils/locales/utilityFunctions";

import {
  APPENDIX_16,
  APPENDIX_25,
  BIOMASS_CATEGORIES,
  FORESTRY_EVALUATION_ASSESSMENT_OPTIONS,
  hasClearcutLikeIntervention,
  hasFellingAndThinningIntervention,
  hasThinningLikeIntervention,
  shouldShowRegenerationAgreement,
} from "./options";
import { ActivityBiomassScreening } from "types/firestoreFeatures";
import YesNoCheckBoxes from "components/input/yesNoCheckboxes";
import BiomassScreeningInterventionField from "./components/biomassScreeningInterventionField";
import RegenerationAgreementField from "./components/regenerationAgreementField";

// Types
interface Props {
  biomassScreeningData: ActivityBiomassScreening;
  inputDisabled: boolean;
  derivedActivityData?: {
    totalArea: number;
    largestArea: number;
  };
  setBiomassScreeningData: ({
    value,
    key,
    parent,
  }: {
    value: any;
    key: string;
    parent?: string;
  }) => void;
  onUploadRegenerationAgreement: (state: boolean, categories?: string[]) => void;
  renderUploadButton: (mode: "light" | "dark", categories: string[]) => JSX.Element;
}

const HarvestSection = ({
  biomassScreeningData,
  inputDisabled,
  derivedActivityData,
  setBiomassScreeningData,
  renderUploadButton,
  onUploadRegenerationAgreement,
}: Props) => {
  const { translate } = useTranslation();

  const selectedActivities = biomassScreeningData?.standAndActivity?.activity ?? [];
  const hasThinningLikeActivity = hasThinningLikeIntervention(selectedActivities);
  const hasClearcutLikeActivity = hasClearcutLikeIntervention(selectedActivities);
  const hasFellingAndThinningActivity = hasFellingAndThinningIntervention(selectedActivities);
  const showRegenerationAgreement = shouldShowRegenerationAgreement(
    biomassScreeningData?.category,
    selectedActivities,
  );

  const ASSESSMENT_OPTIONS = FORESTRY_EVALUATION_ASSESSMENT_OPTIONS.map((id) => {
    return {
      id: id,
      label: translate(`biomass-project-form-forestry-evaluation-assessment-option-${id}`),
    };
  });

  const APPENDIX_16_OPTIONS = APPENDIX_16.map((el) => {
    return { ...el, tooltip: translate(`tooltip-appendix16-value-${el.id}`) };
  });
  const APPENDIX_25_OPTIONS = APPENDIX_25.map((el) => {
    return { ...el, tooltip: translate(`tooltip-appendix25-value-${el.id}`) };
  });

  const renderForest = () => {
    return (
      <Grid item xs={12} sm={8}>
        <Stack spacing={3}>
          <Stack direction={"row"} alignItems="stretch">
            <Box sx={{ mr: 1, alignSelf: "stretch" }}>
              <ToolbarDivider orientation={"horizontal"} />
            </Box>
            <Grid container spacing={{ xs: 2 }}>
              {hasClearcutLikeActivity && (
                <Grid item xs={12}>
                  <RegenerationAgreementField
                    checked={biomassScreeningData?.standAndActivity?.regeneration ?? false}
                    inputDisabled={inputDisabled}
                    agreementRequired={biomassScreeningData?.status?.protectedForest === "yes"}
                    onChange={setBiomassScreeningData}
                    onUpload={onUploadRegenerationAgreement}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography color="secondary">
                  {translate("biomass-project-form-forestry-assessment")}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={biomassScreeningData?.standAndActivity?.expertReview ?? false}
                      onChange={(_, checked) =>
                        setBiomassScreeningData({
                          value: checked,
                          key: "expertReview",
                          parent: "standAndActivity",
                        })
                      }
                      icon={
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            bgcolor: "white",
                            border: inputDisabled ? "1px solid #bfbfbf" : "1px solid #7a7a7a",
                          }}
                        />
                      }
                      disabled={inputDisabled}
                    />
                  }
                  label={translate("biomass-project-form-expert-review")}
                  sx={{ ml: 0, mb: -1 }}
                />
              </Grid>

              {biomassScreeningData?.standAndActivity?.expertReview && (
                <Grid item xs={12} sm={6}>
                  <Typography color="secondary" sx={{ mb: 1 }}>
                    {translate("biomass-project-form-appendix16-value")}
                  </Typography>
                  <Selector
                    title={translate("biomass-project-form-value")}
                    value={
                      biomassScreeningData?.standAndActivity?.appendix16Value
                        ? biomassScreeningData?.standAndActivity?.appendix16Value
                        : ""
                    }
                    options={APPENDIX_16_OPTIONS}
                    label={true}
                    fullWidth={true}
                    changeHandler={(e) =>
                      setBiomassScreeningData({
                        value: e.target.value,
                        key: "appendix16Value",
                        parent: "standAndActivity",
                      })
                    }
                    required={true}
                    disabled={inputDisabled}
                    tooltipPlacement="top-start"
                  />
                </Grid>
              )}
            </Grid>
          </Stack>

          <Stack direction={"row"} alignItems="stretch">
            <Box sx={{ mr: 1, alignSelf: "stretch" }}>
              <ToolbarDivider orientation={"horizontal"} />
            </Box>

            <Grid container spacing={{ xs: 2 }}>
              {hasClearcutLikeActivity && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box>
                      <YesNoCheckBoxes
                        title={translate("biomass-project-form-broadleaves-to-conifer")}
                        yesLabel={translate("biomass-project-form-yes")}
                        noLabel={translate("biomass-project-form-no")}
                        state={biomassScreeningData?.standAndActivity?.broadleavesToConifer}
                        setState={(val) =>
                          setBiomassScreeningData({
                            value: val,
                            key: "broadleavesToConifer",
                            parent: "standAndActivity",
                          })
                        }
                        disabled={inputDisabled}
                      />
                    </Box>
                    {biomassScreeningData?.standAndActivity?.broadleavesToConifer === "yes" && (
                      <Box sx={{ mt: 1, ml: 2 }}>
                        {renderUploadButton("dark", ["standAndActivity", "broadleavesToConifer"])}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography color="secondary">
                  {translate("biomass-project-form-forestry-assessment-title")}
                </Typography>
                <FormGroup sx={{ pl: 1.3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={biomassScreeningData?.standAndActivity?.forestryEvaluation?.includes(
                          "assessment"
                        )}
                        onChange={() =>
                          setBiomassScreeningData({
                            value: "assessment",
                            key: "forestryEvaluation",
                            parent: "standAndActivity",
                          })
                        }
                        icon={
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: "white",
                              border: inputDisabled ? "1px solid #bfbfbf" : "1px solid #7a7a7a",
                            }}
                          />
                        }
                      />
                    }
                    label={translate("biomass-project-form-forestry-assessment")}
                    disabled={
                      (derivedActivityData && derivedActivityData?.largestArea > 5) || inputDisabled
                    }
                  />
                  {hasThinningLikeActivity && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={biomassScreeningData?.standAndActivity?.forestryEvaluation?.includes(
                            "marking"
                          )}
                          onChange={() =>
                            setBiomassScreeningData({
                              value: "marking",
                              key: "forestryEvaluation",
                              parent: "standAndActivity",
                            })
                          }
                          icon={
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                bgcolor: "white",
                                border: inputDisabled ? "1px solid #bfbfbf" : "1px solid #7a7a7a",
                              }}
                            />
                          }
                        />
                      }
                      label={translate("biomass-project-form-forestry-marking")}
                      disabled={inputDisabled}
                    />
                  )}
                </FormGroup>
              </Grid>

              {biomassScreeningData?.standAndActivity?.forestryEvaluation?.includes(
                "assessment"
              ) && (
                <Grid item xs={12} sm={6}>
                  <Typography color="secondary" sx={{ mb: 1 }}>
                    {translate("biomass-project-form-forestry-evaluation-assessment")}
                  </Typography>
                  <Selector
                    title={translate("biomass-project-form-forestry-evaluation-assessment")}
                    value={
                      biomassScreeningData?.standAndActivity?.forestryEvaluationAssessment || ""
                    }
                    options={ASSESSMENT_OPTIONS}
                    label={true}
                    fullWidth={true}
                    changeHandler={(e) =>
                      setBiomassScreeningData({
                        value: e.target.value,
                        key: "forestryEvaluationAssessment",
                        parent: "standAndActivity",
                      })
                    }
                    disabled={inputDisabled}
                    required={true}
                    multiple={false}
                    sx={{ mb: 1 }}
                  />
                </Grid>
              )}
            </Grid>
          </Stack>
        </Stack>
      </Grid>
    );
  };

  const renderNonForest = () => {
    return (
      <Grid item xs={12} sm={8}>
        <Stack spacing={3}>
          {hasFellingAndThinningActivity && (
            <Stack direction={"row"} alignItems="stretch">
              <Box sx={{ mr: 1, alignSelf: "stretch" }}>
                <ToolbarDivider orientation={"horizontal"} />
              </Box>

              {/* Felling and Thinning */}
              <Grid container spacing={{ xs: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography color="secondary" sx={{ mb: 1 }}>
                    {translate("biomass-project-form-appendix25-value")}
                  </Typography>
                  <Selector
                    title={translate("biomass-project-form-value")}
                    value={
                      biomassScreeningData?.standAndActivity?.appendix25Value
                        ? biomassScreeningData?.standAndActivity?.appendix25Value
                        : ""
                    }
                    options={APPENDIX_25_OPTIONS}
                    label={true}
                    fullWidth={true}
                    changeHandler={(e) =>
                      setBiomassScreeningData({
                        value: e.target.value,
                        key: "appendix25Value",
                        parent: "standAndActivity",
                      })
                    }
                    required={true}
                    disabled={inputDisabled}
                    tooltipPlacement="top-start"
                  />
                </Grid>

                <Grid item xs={12} sm={6} />
              </Grid>
            </Stack>
          )}

          {showRegenerationAgreement && (
            <Stack direction={"row"} alignItems="stretch">
              <Box sx={{ mr: 1, alignSelf: "stretch" }}>
                <ToolbarDivider orientation={"horizontal"} />
              </Box>

              {/* Clearing */}
              <Grid container spacing={{ xs: 2 }}>
                <Grid item xs={12}>
                  <RegenerationAgreementField
                    checked={biomassScreeningData?.standAndActivity?.regeneration ?? false}
                    inputDisabled={inputDisabled}
                    onChange={setBiomassScreeningData}
                    onUpload={onUploadRegenerationAgreement}
                  />
                </Grid>
              </Grid>
            </Stack>
          )}
        </Stack>
      </Grid>
    );
  };

  const renderSelection = () => {
    if (
      !biomassScreeningData?.standAndActivity?.activity ||
      biomassScreeningData?.standAndActivity?.activity.length === 0
    )
      return null;
    if (biomassScreeningData?.category === BIOMASS_CATEGORIES[1]) {
      return renderForest();
    } else {
      return renderNonForest();
    }
  };

  return (
    <Grid container spacing={{ xs: 2 }}>
      <BiomassScreeningInterventionField
        data={biomassScreeningData}
        inputDisabled={inputDisabled}
        derivedActivityData={derivedActivityData}
        setData={setBiomassScreeningData}
      />

      {renderSelection()}
    </Grid>
  );
};

export default HarvestSection;
